import React, { useState } from 'react';
import { Box, IconButton, Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';

const NAVIGATION_WIDTH = '250px';
const COLLAPSED_WIDTH = '32px';
const QA_BG = '#203D48';
const TOGGLE_BG = '#5E8A93';
const TOGGLE_HOVER = '#4F7B84';
const STORAGE_KEY = 'qaSidebarVisible';

interface QANavigationShellProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  children: React.ReactNode;
}

const QANavigationShell: React.FC<QANavigationShellProps> = ({
  searchTerm,
  onSearchChange,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const toggleExpansion = () => {
    // @ts-expect-error DD_RUM is not declared on the window type
    window.DD_RUM && window.DD_RUM.addAction('~qa_side_nav_toggled', {});
    const next = !isExpanded;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setIsExpanded(next);
  };

  return (
    <Box position="relative" height="100vh" display="flex" flexShrink={0}>
      {/* Sidebar panel */}
      <Box
        width={isExpanded ? NAVIGATION_WIDTH : COLLAPSED_WIDTH}
        minWidth={isExpanded ? NAVIGATION_WIDTH : COLLAPSED_WIDTH}
        maxWidth={isExpanded ? NAVIGATION_WIDTH : COLLAPSED_WIDTH}
        bg={QA_BG}
        height="100%"
        display="flex"
        flexDirection="column"
        p={0}
        overflow="hidden"
        transition="width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out"
      >
        {/* Search — hidden when collapsed */}
        <Box p="12px" display={isExpanded ? 'block' : 'none'}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="rgba(255,255,255,0.5)" boxSize={3} />
            </InputLeftElement>
            <Input
              pl="7"
              
              placeholder="Search projects..."
              value={searchTerm}
              onChange={e => onSearchChange(e.target.value)}
              bg="rgba(0,0,0,0.2)"
              color="rgba(255,255,255,0.8)"
              border="1px solid rgba(255,255,255,0.2)"
              borderRadius="4px"
              fontSize="12px"
              _placeholder={{ color: 'rgba(255,255,255,0.5)' }}
              _hover={{ border: '1px solid rgba(255,255,255,0.3)' }}
              _focus={{ border: '1px solid #E5927D', boxShadow: 'none' }}
            />
          </InputGroup>
        </Box>

        {/* Content — hidden when collapsed */}
        <Box
          flex={1}
          overflowY="auto"
          overflowX="hidden"
          display={isExpanded ? 'block' : 'none'}
        >
          {children}
        </Box>
      </Box>

      {/* Toggle button */}
      <IconButton
        aria-label="toggle-sidebar"
        icon={isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        onClick={toggleExpansion}
        position="absolute"
        top="60px"
        right="-16px"
        transform="translateY(-50%)"
        bg={TOGGLE_BG}
        color="white"
        width="24px"
        height="24px"
        minWidth="24px"
        borderRadius="full"
        boxShadow="sm"
        zIndex={1}
        size="xs"
        _hover={{ bg: TOGGLE_HOVER }}
      />
    </Box>
  );
};

export default QANavigationShell;
